/**
 * Phase 101ga (Feb 2026) — Vague 4 : dialog de fusion de 2 Parties.
 *
 * Ouvert depuis :
 *  - PartyDetailPanel : bouton "Fusionner avec…" (choix libre du target)
 *  - DuplicatesPanel : bouton "Fusionner" sur une paire de doublons
 *
 * UX :
 *  - Preview côte-à-côte des 2 Parties (source à absorber → target)
 *  - Toggle "Remplir les champs vides de la cible depuis la source" (défaut ON)
 *  - Confirmation explicite : "L'ID de la source deviendra un alias.
 *    Toutes les sources liées seront transférées vers la cible."
 *  - Toast succès : ✅ Party fusionnée. N sources déplacées, M champs remplis.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, Building2, User, Mail, Phone, MapPin, Loader2,
  AlertTriangle, GitMerge,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

function PartyPreview({ party, badge, tone }) {
  if (!party) {
    return (
      <div className="flex items-center justify-center py-8 text-[#A3A6A3]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  return (
    <div className={`border rounded-lg p-3 ${tone}`}>
      <div className="flex items-center justify-between mb-2">
        <Badge variant="outline" className="text-[10px]">{badge}</Badge>
        {party.kind === "organization"
          ? <Building2 className="w-4 h-4 text-[#575A57]" />
          : <User className="w-4 h-4 text-[#575A57]" />}
      </div>
      <div className="font-medium text-[#1A1C1A] text-sm leading-tight">
        {party.display_name}
      </div>
      {party.legal_name && party.legal_name !== party.display_name && (
        <div className="text-xs text-[#575A57] mt-0.5">{party.legal_name}</div>
      )}
      <div className="mt-2 space-y-1 text-xs text-[#575A57]">
        {party.email && (
          <div className="flex items-center gap-1">
            <Mail className="w-3 h-3" /> {party.email}
          </div>
        )}
        {party.phone && (
          <div className="flex items-center gap-1">
            <Phone className="w-3 h-3" /> {party.phone}
          </div>
        )}
        {(party.city || party.postal_code) && (
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[party.postal_code, party.city].filter(Boolean).join(" ")}
          </div>
        )}
        <div className="pt-1 text-[10px] italic">
          {(party.linked_sources || []).length} source(s) liée(s)
        </div>
      </div>
    </div>
  );
}

export function PartyMergeDialog({ open, onClose, sourceParty, onMerged }) {
  const [candidates, setCandidates] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [target, setTarget] = useState(null);
  const [fillFromSource, setFillFromSource] = useState(true);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const loadCandidates = async (searchStr) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (searchStr) params.search = searchStr;
      const { data } = await api.get("/admin/parties", { params });
      setCandidates(data.items.filter((p) => p.id !== sourceParty?.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement candidats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && sourceParty) {
      // Charge par défaut les candidats avec le même email OU même display_name
      const initial = sourceParty.email
        ? sourceParty.email
        : (sourceParty.display_name || "").split(" ")[0];
      loadCandidates(initial);
      setTargetId("");
      setTarget(null);
    }
    /* eslint-disable-next-line */
  }, [open, sourceParty?.id]);

  useEffect(() => {
    if (!targetId) { setTarget(null); return; }
    api.get(`/admin/parties/${targetId}`)
      .then(({ data }) => setTarget(data))
      .catch(() => setTarget(null));
  }, [targetId]);

  const handleMerge = async () => {
    if (!targetId || !sourceParty) return;
    setMerging(true);
    try {
      const { data } = await api.post(
        `/admin/parties/${targetId}/merge`,
        { source_id: sourceParty.id, fill_from_source: fillFromSource }
      );
      toast.success(
        `✅ Party fusionnée · ${data.sources_moved} source(s) déplacée(s), ${data.backrefs_rewritten} lien(s) réécrit(s)${data.fields_filled.length ? `, ${data.fields_filled.length} champ(s) rempli(s)` : ""}`,
        { duration: 6000 }
      );
      onMerged?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur fusion";
      toast.error(detail);
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl"
        data-testid="party-merge-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[#1F4E3D]" />
            Fusionner deux Parties
          </DialogTitle>
          <DialogDescription className="text-xs text-[#575A57]">
            La source sera absorbée par la cible : ses sources liées seront ré-attachées,
            son ID deviendra un alias. Stratégie fill_if_empty (aucun écrasement).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-11 gap-3 items-center">
          <div className="col-span-5">
            <div className="text-[10px] uppercase tracking-wider text-[#8C3A2A] mb-1">
              À absorber (deviendra alias)
            </div>
            <PartyPreview
              party={sourceParty}
              badge="Source"
              tone="border-[#F4CBB0] bg-[#FDF3EA]"
            />
          </div>
          <div className="col-span-1 flex justify-center">
            <ArrowRight className="w-6 h-6 text-[#8C6B2F]" />
          </div>
          <div className="col-span-5">
            <div className="text-[10px] uppercase tracking-wider text-[#1F4E3D] mb-1">
              Cible (conserve son ID)
            </div>
            <PartyPreview
              party={target}
              badge="Cible"
              tone="border-[#B7D0BF] bg-[#F0F7F2]"
            />
          </div>
        </div>

        {/* Sélecteur cible */}
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1">
            Choisis la Party cible
          </div>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger data-testid="party-merge-target-select">
              <SelectValue placeholder={loading ? "Chargement…" : "Sélectionner…"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {candidates.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.display_name}
                  {p.email ? ` · ${p.email}` : ""}
                  {p.city ? ` · ${p.city}` : ""}
                </SelectItem>
              ))}
              {candidates.length === 0 && !loading && (
                <div className="px-2 py-2 text-xs text-[#A3A6A3]">
                  Aucun candidat.
                </div>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Options */}
        <div className="mt-3 flex items-start gap-3 p-3 rounded-md bg-[#FBF4E9] border border-[#E9D9B7]">
          <Switch
            checked={fillFromSource}
            onCheckedChange={setFillFromSource}
            data-testid="party-merge-fill-toggle"
          />
          <div className="flex-1 text-xs text-[#1A1C1A]">
            <div className="font-medium">Compléter les champs vides de la cible</div>
            <div className="text-[#575A57] mt-0.5">
              Si activé, les champs vides de la cible (téléphone, adresse, ville…)
              seront remplis depuis la source. Les valeurs déjà présentes ne sont
              JAMAIS écrasées.
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 p-3 rounded-md bg-[#FEF5F3] border border-[#F4D4CC] text-xs text-[#8C3A2A]">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Action irréversible côté fusion.</strong> Toutes les sources
            liées (distributeurs, revendeurs, devis…) pointant vers la source
            seront ré-attachées à la cible. La source sera masquée mais
            conservée en base (statut « fusionnée »).
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={merging}>
            Annuler
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!targetId || merging}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="party-merge-confirm-btn"
          >
            {merging
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Fusion…</>
              : <><GitMerge className="w-4 h-4 mr-1" /> Fusionner dans la cible</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
